class FluffCartPillowOffer extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.addButton = this.querySelector('[data-fluff-cart-pillow-add]');
    this.message = this.querySelector('[data-fluff-cart-pillow-message]');
    this.dataElement = this.querySelector('[data-fluff-cart-pillow-data]');
    if (!this.addButton || !this.dataElement) return;

    try {
      this.data = JSON.parse(this.dataElement.textContent);
    } catch (error) {
      this.setMessage(this.dataset.loadError);
      return;
    }

    this.abortController = new AbortController();
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    this.addButton.disabled = true;
    this.addButton.addEventListener('click', () => this.addPillowBundle(), { signal: this.abortController.signal });
    this.verifyEligibility();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.isSubmitting = false;
  }

  setMessage(message = '') {
    if (this.message) this.message.textContent = message;
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    this.addButton.disabled = isSubmitting || this.hasExistingVariant;
    this.addButton.toggleAttribute('aria-busy', isSubmitting);
  }

  waitForCartRender(eventName) {
    return new Promise((resolve) => {
      let timeoutId;
      const finish = () => {
        window.clearTimeout(timeoutId);
        document.removeEventListener(eventName, finish);
        resolve();
      };

      document.addEventListener(eventName, finish, { once: true });
      timeoutId = window.setTimeout(finish, 2500);
    });
  }

  async fetchCart(root) {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart state could not be loaded.');
    return response.json();
  }

  applyCartEligibility(cart) {
    const eligibleProductIds = new Set((this.data?.eligibleProductIds || []).map(String));
    const hasEligibleMattress = cart?.items?.some((item) => eligibleProductIds.has(String(item.product_id)));
    const hasPillowBundle = cart?.items?.some((item) => String(item.variant_id) === String(this.data?.id));

    if (!hasEligibleMattress || hasPillowBundle) {
      this.remove();
      return false;
    }

    this.hidden = false;
    this.removeAttribute('aria-hidden');
    this.setSubmitting(false);
    return true;
  }

  async verifyEligibility() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      this.applyCartEligibility(await this.fetchCart(root));
    } catch (error) {
      this.remove();
    }
  }

  async addPillowBundle() {
    if (this.isSubmitting || FluffCartPillowOffer.isAdding || !this.data?.id) return;

    const cartContainer = this.closest('hdt-cart-drawer, hdt-main-cart');
    const cartSectionId = cartContainer?.getAttribute('section-id') || this.dataset.sectionId;
    if (!cartContainer || !cartSectionId) {
      this.setMessage(this.dataset.updateError);
      return;
    }
    const cartSectionIds = [...new Set(
      Array.from(document.querySelectorAll('hdt-cart-drawer[section-id], hdt-main-cart[section-id]'))
        .map((element) => element.getAttribute('section-id'))
        .filter(Boolean)
    )];
    if (!cartSectionIds.includes(cartSectionId)) cartSectionIds.push(cartSectionId);
    const renderEvent = cartContainer.matches('hdt-main-cart') ? 'cart:main:render' : 'cart:drawer:render';

    const payload = {
      items: [{
        id: this.data.id,
        quantity: 1
      }],
      sections: cartSectionIds,
      sections_url: `${window.location.pathname}${window.location.search}`
    };

    FluffCartPillowOffer.isAdding = true;
    this.setSubmitting(true);
    this.setMessage('');
    let addSucceeded = false;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentCart = await this.fetchCart(root);
      if (currentCart.items.some((item) => String(item.variant_id) === String(this.data.id))) {
        this.hasExistingVariant = true;
        this.setMessage(this.dataset.alreadyAdded);
        return;
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const cartData = await response.json();
      if (!response.ok || cartData.status) {
        const error = new Error(cartData.description || cartData.message || 'Cart error');
        error.isSoldOut = response.status === 422;
        throw error;
      }
      addSucceeded = true;
      this.hasExistingVariant = true;

      const latestCart = await this.fetchCart(root);
      const addedLine = latestCart.items.find((item) => String(item.variant_id) === String(this.data.id));
      if (!addedLine || addedLine.quantity !== 1 || !cartData.sections?.[cartSectionId]) {
        throw new Error('Updated cart section is unavailable.');
      }

      const cartRenderPromise = this.waitForCartRender(renderEvent);
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: {
          resource: latestCart,
          sourceId: this.id || `fluff-cart-pillow-${this.dataset.sectionId}`,
          cartData,
          actionAfterATC: 'no_action',
          source: 'fluff-cart-pillow-offer',
          data: {
            itemCount: latestCart.item_count,
            variantId: addedLine.variant_id,
            sections: cartData.sections
          }
        }
      }));
      document.dispatchEvent(new CustomEvent('cart:count:update', { detail: { item_count: latestCart.item_count } }));
      await cartRenderPromise;
    } catch (error) {
      if (error.isSoldOut) {
        this.setMessage(this.dataset.soldOutError);
      } else {
        this.setMessage(addSucceeded ? this.dataset.updateError : this.dataset.addError);
      }
    } finally {
      FluffCartPillowOffer.isAdding = false;
      this.setSubmitting(false);
    }
  }
}

FluffCartPillowOffer.isAdding = false;

if (!customElements.get('fluff-cart-pillow-offer')) customElements.define('fluff-cart-pillow-offer', FluffCartPillowOffer);

const formatFluffCartMoney = (amount) => {
  const value = Math.max(0, Number(amount) || 0) / 100;
  return `LE ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(value)}`;
};

const getFluffCartShippingState = (cart, eligibleProductIds, shippingTarget, aovVariantId, freeShippingProductIds = []) => {
  const eligibleIds = new Set(eligibleProductIds.map(String));
  const hasEligibleMattress = cart.items.some((item) => eligibleIds.has(String(item.product_id)));
  const hasAovPillows = Boolean(aovVariantId) && cart.items.some((item) => String(item.variant_id) === String(aovVariantId));
  const totalPrice = Math.max(0, Number(cart.total_price) || 0);
  const isAovUpsell = hasEligibleMattress && !hasAovPillows;
  const freeIds = new Set(freeShippingProductIds.map(String));
  const ribbedOnly = cart.items.length > 0 && cart.items.every((item) => freeIds.has(String(item.product_id)));
  const isComplete = ribbedOnly || (hasEligibleMattress ? hasAovPillows : totalPrice >= shippingTarget);
  return {
    ribbedOnly,
    hasEligibleMattress,
    hasAovPillows,
    isAovUpsell,
    isComplete,
    progressRate: isAovUpsell ? 0 : isComplete ? 1 : Math.min(totalPrice / shippingTarget, 1),
    remaining: Math.max(shippingTarget - totalPrice, 0),
    totalPrice
  };
};

const syncFluffCartState = (cart) => {
  if (!cart?.items || typeof cart.total_price !== 'number') return false;

  document.querySelectorAll('[data-fluff-cart-incentives]').forEach((incentives) => {
    let eligibleProductIds = [];
    try {
      eligibleProductIds = JSON.parse(incentives.dataset.fluffEligibleProductIds || '[]');
    } catch (error) {
      eligibleProductIds = [];
    }

    const shippingTarget = Number(incentives.dataset.fluffShippingTarget) || 300000;
    const freeShippingProductIds = JSON.parse(incentives.dataset.fluffFreeShippingProductIds || '[]');
    const state = getFluffCartShippingState(cart, eligibleProductIds, shippingTarget, incentives.dataset.fluffAovVariantId, freeShippingProductIds);
    const message = incentives.querySelector('[data-fluff-shipping-message]');
    const progressBar = incentives.querySelector('[data-fluff-progress-rate]');
    const values = incentives.querySelector('[data-fluff-shipping-values]');
    const currentValue = incentives.querySelector('[data-fluff-shipping-current]');
    const targetValue = incentives.querySelector('[data-fluff-shipping-target]');
    const support = incentives.querySelector('[data-fluff-shipping-support]');

    incentives.classList.toggle('is-complete', state.isComplete);
    incentives.classList.toggle('is-incomplete', !state.isComplete);
    incentives.classList.toggle('is-aov-upsell', state.isAovUpsell);
    if (message) {
      message.innerHTML = state.isComplete
        ? incentives.dataset.fluffCompleteMessage
        : state.isAovUpsell
          ? incentives.dataset.fluffAovUpsellMessage
          : incentives.dataset.fluffRemainingTemplate.replace('__REMAINING__', formatFluffCartMoney(state.remaining));
    }
    if (progressBar) {
      progressBar.dataset.fluffProgressRate = String(state.progressRate);
      progressBar.style.setProperty('--progress-rate', String(state.progressRate));
      progressBar.setAttribute('aria-valuenow', String(state.isComplete ? shippingTarget : state.isAovUpsell ? 0 : state.totalPrice));
    }
    if (values) values.hidden = state.hasEligibleMattress || state.ribbedOnly;
    if (currentValue) currentValue.textContent = formatFluffCartMoney(state.totalPrice);
    if (targetValue) targetValue.textContent = formatFluffCartMoney(shippingTarget);
    if (support) support.hidden = state.hasEligibleMattress || state.ribbedOnly;
  });

  document.querySelectorAll('fluff-cart-pillow-offer').forEach((offer) => offer.applyCartEligibility(cart));
  return true;
};

const refreshFluffCartState = async () => {
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    syncFluffCartState(await response.json());
  } catch (error) {
    return;
  }
};

const handleFluffCartUpdate = (event) => {
  if (!syncFluffCartState(event.detail?.resource)) refreshFluffCartState();
};

document.addEventListener('cart:update', handleFluffCartUpdate);
document.addEventListener('cart:drawer:render', refreshFluffCartState);
document.addEventListener('cart:main:render', refreshFluffCartState);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshFluffCartState, { once: true });
} else {
  refreshFluffCartState();
}


